import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings, LogIn, User, CreditCard, LogOut, HelpCircle, FileText, Database, Menu, ShieldAlert, TrendingUp, BarChart3, Sparkles, BookOpen, DollarSign, Zap, ClipboardList, AlertTriangle, FileCheck, PenLine, Library, FolderKanban } from "lucide-react";
import ThemeToggle from "@/components/layout/ThemeToggle";
import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/NavLink";
import { supabase } from "@/integrations/supabase/client";
import { useThemedLogo } from "@/hooks/useThemedLogo";
import { useUser } from "@/hooks/useUser";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { clearUserScopedStorage } from "@/lib/auth-utils";
import exosLogoFallback from "@/assets/exos-logo-dark.svg";
import { SCENARIO_COUNT_SHORT } from "@/lib/seo/constants";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
} from "@/components/ui/navigation-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";

// -- Navigation data ----------------------------------------------------------

type NavItem = {
  label: string;
  path: string;
  icon: typeof BarChart3;
  description?: string;
};

type NavFeature = {
  eyebrow: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaPath: string;
  icon: typeof Sparkles;
};

type NavSection = {
  heading: string;
  items: NavItem[];
};

type NavGroup = {
  label: string;
  items: NavItem[];
  sections?: NavSection[];
  feature?: NavFeature;
};

const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Scenarios",
    items: [
      { label: "Analysis", path: "/#category-analysis", icon: BarChart3, description: "TCO, should-cost & cost waterfalls" },
      { label: "Planning", path: "/#category-planning", icon: ClipboardList, description: "Make-or-buy & sourcing strategy" },
      { label: "Risk", path: "/#category-risk", icon: AlertTriangle, description: "Supplier, geopolitical & black swan" },
      { label: "Documentation", path: "/#category-documentation", icon: FileCheck, description: "SOW reviews & negotiation prep" },
    ],
    sections: [
      {
        heading: "Projects",
        items: [
          { label: "Projects workspace", path: "/projects", icon: FolderKanban, description: "Load your scenario parameters as a procurement project" },
        ],
      },
    ],
    feature: {
      eyebrow: "Featured",
      title: `${SCENARIO_COUNT_SHORT.replace("scenarios", "Scenarios")} Library`,
      description: "Human-in-the-loop analyses for the procurement decisions that matter most.",
      ctaLabel: "Explore scenarios",
      ctaPath: "/",
      icon: Sparkles,
    },
  },
  {
    label: "Analytical Platforms",
    items: [
      { label: "Risk Assessment Platform", path: "/enterprise/risk", icon: ShieldAlert, description: "Continuous monitoring of suppliers & exposure" },
      { label: "Inflation Analysis Platform", path: "/enterprise/inflation", icon: TrendingUp, description: "Driver-level signal radar for cost shifts" },
    ],
    feature: {
      eyebrow: "New",
      title: "Delta-First Monitoring",
      description: "Catch material changes early — focus your team on what actually moved.",
      ctaLabel: "See how it works",
      ctaPath: "/features",
      icon: TrendingUp,
    },
  },
  {
    label: "About EXOS",
    items: [
      { label: "Technology & AI", path: "/features", icon: Sparkles, description: "Agentic workflow & grounding stack" },
      { label: "Blog", path: "/blog", icon: PenLine, description: "Procurement insights & playbooks" },
      { label: "Pricing", path: "/pricing", icon: DollarSign, description: "Plans for individual buyers to enterprise teams" },
      { label: "Help & FAQ", path: "/pricing#faq", icon: HelpCircle, description: "Answers to common questions" },
    ],
    feature: {
      eyebrow: "Get started",
      title: "ROI from day one",
      description: "Transparent pricing, no consultants needed. Start with a 30-day free trial.",
      ctaLabel: "View pricing",
      ctaPath: "/pricing",
      icon: DollarSign,
    },
  },
] as const;

// Smart navigation that handles hash links to the current page
const HEADER_OFFSET = 96;
const scrollToHashId = (hashPart: string) => {
  const el = document.getElementById(hashPart);
  if (!el) return false;
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top, behavior: "smooth" });
  return true;
};
const navigateWithHash = (path: string, navigate: (p: string) => void) => {
  const hashIndex = path.indexOf("#");
  if (hashIndex !== -1) {
    const [pathPart, hashPart] = [path.slice(0, hashIndex) || "/", path.slice(hashIndex + 1)];
    if (window.location.pathname === pathPart) {
      if (scrollToHashId(hashPart)) {
        window.history.replaceState(null, "", `${pathPart}#${hashPart}`);
        return;
      }
    }
  }
  navigate(path);
};

// Shared mega-dropdown content renderer
const MegaDropdown = ({ group, navigate }: { group: NavGroup; navigate: (path: string) => void }) => {
  return (
    <div className="grid w-[420px] grid-cols-2">
      <div className="col-span-2 p-5">
        <p className="font-display text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-3">
          {group.label}
        </p>
        <ul className="grid grid-cols-2 gap-1.5">
          {group.items.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <button
                  onClick={() => navigateWithHash(item.path, navigate)}
                  type="button"
                  className="group flex items-start gap-3 w-full rounded-lg p-2.5 hover:bg-primary/10 hover:ring-1 hover:ring-primary/30 focus-visible:bg-primary/10 focus-visible:ring-1 focus-visible:ring-primary/30 focus-visible:outline-none transition-colors text-left"
                >
                  <div className="shrink-0 w-9 h-9 rounded-md bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center text-primary transition-colors">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-foreground leading-tight">
                      {item.label}
                    </div>
                    {item.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                        {item.description}
                      </p>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        {group.sections?.map((section) => (
          <div key={section.heading} className="mt-4 pt-4 border-t border-border/60">
            <p className="font-display text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-3">
              {section.heading}
            </p>
            <ul className="grid grid-cols-1 gap-1.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => navigateWithHash(item.path, navigate)}
                      type="button"
                      className="group flex items-start gap-3 w-full rounded-lg p-2.5 hover:bg-primary/10 hover:ring-1 hover:ring-primary/30 focus-visible:bg-primary/10 focus-visible:ring-1 focus-visible:ring-primary/30 focus-visible:outline-none transition-colors text-left"
                    >
                      <div className="shrink-0 w-9 h-9 rounded-md bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center text-primary transition-colors">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm text-foreground leading-tight">
                          {item.label}
                        </div>
                        {item.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

// -- Personal greeting --------------------------------------------------------

const getPersonalGreeting = (firstName: string): { greeting: string; subtitle: string } => {
  const now = new Date();
  const hour = now.getHours();
  const day = now.getDay(); // 0=Sun..6=Sat
  const date = now.getDate();
  const month = now.getMonth(); // 0=Jan
  const name = firstName || "there";

  // Special dates first
  if (month === 0 && date === 1) return { greeting: `Happy New Year, ${name}! 🎉`, subtitle: "Fresh start, fresh wins." };
  if (month === 11 && date >= 24 && date <= 26) return { greeting: `Happy holidays, ${name} 🎄`, subtitle: "Hope you're taking it easy." };
  if (month === 11 && date === 31) return { greeting: `Last day of the year, ${name}!`, subtitle: "One more push, then celebrate." };

  // Day-of-week vibes
  if (day === 5) {
    if (hour < 12) return { greeting: `Happy Friday, ${name} 🙌`, subtitle: "Almost the weekend — let's wrap it up." };
    return { greeting: `Friday afternoon, ${name}`, subtitle: "Sneak in one last win before the weekend." };
  }
  if (day === 6) return { greeting: `Working on a Saturday, ${name}? 💪`, subtitle: "Respect. Make it count." };
  if (day === 0) return { greeting: `Sunday vibes, ${name} ☕`, subtitle: "Easing into the week ahead?" };
  if (day === 1) {
    if (hour < 12) return { greeting: `Monday mode, ${name} ☕`, subtitle: "Let's set the tone for the week." };
    return { greeting: `Powering through Monday, ${name}`, subtitle: "You've got this." };
  }
  if (day === 3) return { greeting: `Hump day, ${name} 🐫`, subtitle: "Halfway there." };
  if (day === 4) return { greeting: `Almost Friday, ${name}`, subtitle: "Keep the momentum going." };

  // Generic time-of-day
  if (hour < 5) return { greeting: `Burning the midnight oil, ${name}? 🌙`, subtitle: "Don't forget to rest." };
  if (hour < 12) return { greeting: `Good morning, ${name} ☀️`, subtitle: "What are we tackling today?" };
  if (hour < 17) return { greeting: `Good afternoon, ${name}`, subtitle: "Hope the day's treating you well." };
  if (hour < 22) return { greeting: `Good evening, ${name} 🌆`, subtitle: "Wrapping up or just getting started?" };
  return { greeting: `Late night, ${name}? 🌙`, subtitle: "Make it a productive one." };
};

// -- Component ----------------------------------------------------------------

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { isSuperAdmin } = useAdminAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [firstName, setFirstName] = useState<string>("");
  const exosLogo = useThemedLogo();

  // Fetch first name from profile for personalised greeting
  useEffect(() => {
    if (!user?.id) {
      setFirstName("");
      return;
    }
    let cancelled = false;
    supabase
      .from("profiles")
      .select("full_name, display_name")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        // Prefer real names over display_name (which may have been seeded with
        // the email address). Never appeal to an email — derive a first name
        // by taking the leading token of the local-part.
        const isEmail = (s: string) => /\S+@\S+\.\S+/.test(s);
        const candidates = [data?.full_name, data?.display_name].filter(
          (s): s is string => !!s && !isEmail(s)
        );
        const raw = candidates[0] || user.email?.split("@")[0] || "";
        const first = raw.trim().split(/[\s._-]+/)[0] || "";
        setFirstName(first.charAt(0).toUpperCase() + first.slice(1));
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.email]);

  const personalGreeting = useMemo(() => getPersonalGreeting(firstName), [firstName]);

  // Close transient navigation UI on route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search, location.hash]);

  const mobileNavigate = (path: string) => {
    setMobileOpen(false);
    // If guest tries to open a protected scenarios anchor on `/`, the Index
    // route will redirect to /welcome and drop the hash. Send them through
    // /auth with a redirect so they land on the right section after login.
    const isProtectedHomeAnchor = path.startsWith("/#") || path === "/";
    if (!user && isProtectedHomeAnchor) {
      setTimeout(() => navigate(`/auth?redirect=${encodeURIComponent(path)}`), 50);
      return;
    }
    setTimeout(() => navigateWithHash(path, navigate), 50);
  };

  return (
    <header className="sticky top-0 z-50 mx-3 md:mx-6 rounded-b-2xl bg-background/80 dark:bg-card/95 backdrop-blur-md border border-t-0 border-border/50 dark:border-border shadow-lg dark:shadow-2xl dark:shadow-black/40">
      <div className="px-4 md:px-6 flex h-16 items-center justify-between">
        {/* Logo */}
        <NavLink to="/welcome" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
          <img src={exosLogo} alt="EXOS procurement platform logo" width={40} height={40} className="h-9 md:h-10 w-auto object-contain" decoding="async" />
          <div className="h-7 w-px bg-border/60" />
          <span className="font-display font-bold text-2xl md:text-[28px] tracking-[0.15em] text-foreground leading-none">
            EXOS
          </span>
        </NavLink>

        {/* Desktop Mega-Menu */}
        <div className="hidden md:flex items-center gap-1">
          {NAV_GROUPS.slice(0, 2).map((group) => (
            <NavigationMenu key={group.label}>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className="font-display font-semibold tracking-tight text-[15px] text-foreground/80 bg-transparent hover:bg-muted hover:text-foreground data-[state=open]:!bg-muted data-[state=open]:!text-foreground focus:!bg-muted focus:!text-foreground"
                    onClick={() => {
                      if (group.label === "Scenarios") {
                        navigate("/");
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                  >
                    {group.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <MegaDropdown group={group} navigate={navigate} />
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          ))}

          <button
            onClick={() => navigate("/market-intelligence")}
            className="font-display font-semibold tracking-tight text-[15px] text-foreground/80 px-3 py-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Market Intelligence
          </button>

          {NAV_GROUPS.slice(2).map((group) => (
            <NavigationMenu key={group.label}>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className="font-display font-semibold tracking-tight text-[15px] text-foreground/80 bg-transparent hover:bg-muted hover:text-foreground data-[state=open]:!bg-muted data-[state=open]:!text-foreground focus:!bg-muted focus:!text-foreground"
                  >
                    {group.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <MegaDropdown group={group} navigate={navigate} />
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          ))}
        </div>

        <div className="flex items-center gap-1">
          {location.pathname !== "/welcome" && <ThemeToggle />}

          {/* Mobile hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 overflow-y-auto">
              <SheetHeader className="mb-4">
                <SheetTitle className="font-display text-lg">EXOS</SheetTitle>
              </SheetHeader>

              <Accordion type="multiple" className="w-full">
                {NAV_GROUPS.slice(0, 2).map((group) => (
                  <AccordionItem key={group.label} value={group.label}>
                    <AccordionTrigger className="text-sm font-medium text-foreground py-2.5 px-3 hover:no-underline hover:bg-muted rounded-md">
                      {group.label}
                    </AccordionTrigger>
                    <AccordionContent className="pb-1 pt-0">
                      <div className="flex flex-col gap-0.5 pl-3">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={item.path}
                              onClick={() => mobileNavigate(item.path)}
                              className="text-sm text-muted-foreground py-2 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                            >
                              {Icon && <Icon className="w-4 h-4 text-primary" />}
                              {item.label}
                            </button>
                          );
                        })}
                        {group.sections?.map((section) => (
                          <div key={section.heading} className="mt-2 pt-2 border-t border-border/60">
                            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground px-3 py-1">
                              {section.heading}
                            </p>
                            {section.items.map((item) => {
                              const Icon = item.icon;
                              return (
                                <button
                                  key={item.path}
                                  onClick={() => mobileNavigate(item.path)}
                                  className="text-sm text-muted-foreground py-2 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2 w-full"
                                >
                                  {Icon && <Icon className="w-4 h-4 text-primary" />}
                                  {item.label}
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <button
                onClick={() => mobileNavigate("/market-intelligence")}
                className="text-sm font-medium text-foreground py-2.5 px-3 hover:bg-muted rounded-md text-left flex items-center gap-2 w-full"
              >
                <Sparkles className="w-4 h-4 text-primary" />
                Market Intelligence
              </button>

              <Accordion type="multiple" className="w-full">
                {NAV_GROUPS.slice(2).map((group) => (
                  <AccordionItem key={group.label} value={group.label}>
                    <AccordionTrigger className="text-sm font-medium text-foreground py-2.5 px-3 hover:no-underline hover:bg-muted rounded-md">
                      {group.label}
                    </AccordionTrigger>
                    <AccordionContent className="pb-1 pt-0">
                      <div className="flex flex-col gap-0.5 pl-3">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={item.path}
                              onClick={() => mobileNavigate(item.path)}
                              className="text-sm text-muted-foreground py-2 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                            >
                              {Icon && <Icon className="w-4 h-4 text-primary" />}
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <Separator className="my-4" />

              {user ? (
                <div className="flex flex-col gap-1">
                  <div className="px-3 py-2 mb-1">
                    <p className="text-sm font-medium text-foreground truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={() => mobileNavigate("/account")}
                    className="text-sm text-muted-foreground py-2.5 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                  >
                    <User className="w-4 h-4" /> My Account
                  </button>
                  <button
                    onClick={() => mobileNavigate("/features#procurement-ecosystem")}
                    className="text-sm text-muted-foreground py-2.5 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" /> My Reports
                  </button>
                  <button
                    onClick={() => mobileNavigate("/report-library")}
                    className="text-sm text-muted-foreground py-2.5 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                  >
                    <Library className="w-4 h-4" /> My reports
                  </button>
                  <button
                    onClick={() => mobileNavigate("/pricing#faq")}
                    className="text-sm text-muted-foreground py-2.5 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                  >
                    <HelpCircle className="w-4 h-4" /> Help & FAQ
                  </button>
                  {isSuperAdmin && (
                    <>
                      <Separator className="my-2" />
                      <button
                        onClick={() => mobileNavigate("/admin/methodology")}
                        className="text-sm text-muted-foreground py-2.5 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                      >
                        <BookOpen className="w-4 h-4" /> Methodology
                      </button>
                      <button
                        onClick={() => mobileNavigate("/admin/dashboard")}
                        className="text-sm text-muted-foreground py-2.5 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                      >
                        <BarChart3 className="w-4 h-4" /> Command Center
                      </button>
                      <button
                        onClick={() => mobileNavigate("/admin/analytics")}
                        className="text-sm text-muted-foreground py-2.5 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                      >
                        <Zap className="w-4 h-4" /> Analytics
                      </button>
                    </>
                  )}
                  <Separator className="my-2" />
                  <button
                    onClick={async () => {
                      clearUserScopedStorage();
                      await supabase.auth.signOut();
                      mobileNavigate("/");
                    }}
                    className="text-sm text-destructive py-2.5 px-3 rounded-md hover:bg-muted text-left flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </div>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  className="w-full gap-2"
                  onClick={() => mobileNavigate("/auth")}
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </Button>
              )}
            </SheetContent>
          </Sheet>

          {/* Desktop user menu */}
          {user ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="ml-2 w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-sm font-semibold text-primary-foreground cursor-pointer hover:opacity-90 transition-opacity outline-none">
                  {user.email?.charAt(0).toUpperCase() || "U"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="px-3 py-2.5">
                  <p className="font-display text-sm font-semibold text-foreground leading-tight">
                    {personalGreeting.greeting}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    {personalGreeting.subtitle}
                  </p>
                  <p className="text-[11px] text-muted-foreground/70 truncate mt-1.5">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => navigate("/account")}>
                  <User className="w-4 h-4" /> My Account
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => navigate("/report-library")}>
                  <FileText className="w-4 h-4" /> My Reports
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => navigate("/market-intelligence?tab=insights")}>
                  <Database className="w-4 h-4" /> My Knowledge Database
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => navigate("/pricing#faq")}>
                  <HelpCircle className="w-4 h-4" /> Help & FAQ
                </DropdownMenuItem>
                {isSuperAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => navigate("/admin/methodology")}>
                      <BookOpen className="w-4 h-4" /> Methodology
                    </DropdownMenuItem>
                    <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => navigate("/admin/dashboard")}>
                      <BarChart3 className="w-4 h-4" /> Command Center
                    </DropdownMenuItem>
                    <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => navigate("/admin/analytics")}>
                      <Zap className="w-4 h-4" /> Analytics
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer gap-2 text-destructive focus:text-destructive"
                  onClick={async () => {
                    clearUserScopedStorage();
                    await supabase.auth.signOut();
                    navigate("/");
                  }}
                >
                  <LogOut className="w-4 h-4" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <NavLink to="/auth" className="hidden md:inline-flex">
              <Button variant="default" size="sm" className="ml-2 gap-2">
                <LogIn className="w-4 h-4" />
                Sign In
              </Button>
            </NavLink>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
